import {
	Button,
	CloseButton,
	Dialog,
	Portal,
	type DialogBodyProps,
	type DialogContentProps,
	type DialogFooterProps,
	type DialogHeaderProps,
	type DialogRootProps,
	type DialogTriggerProps,
} from '@chakra-ui/react';
import type { PropsWithChildren } from 'react';

type CustomDialogContentProps = {
	children: React.ReactNode;
	ref?:     React.Ref<HTMLDivElement>;
} & DialogContentProps;

const CustomDialogContent = ({ children, ref, ...dialogContentProps }: CustomDialogContentProps) => {
	return <Dialog.Content ref={ref} {...dialogContentProps}>{children}</Dialog.Content>;
};

type CustomDialogHeaderProps = {
	title: React.ReactNode;
} & DialogHeaderProps;

const CustomDialogHeader = ({ title, ...customDialogHeaderProps }: CustomDialogHeaderProps) => {
	return (
		<Dialog.Header
			alignItems={'center'}
			display={'flex'}
			justifyContent={'space-between'}
			{...customDialogHeaderProps}
		>
			<Dialog.Title>{title}</Dialog.Title>
			<Dialog.CloseTrigger asChild>
				<CloseButton size={'sm'} />
			</Dialog.CloseTrigger>
		</Dialog.Header>
	);
};

const CustomDialogBody = ({ children, ...customDialogBodyProps }: DialogBodyProps) => {
	return <Dialog.Body {...customDialogBodyProps}>{children}</Dialog.Body>;
};

type CustomDialogFooterProps = {
	cancelButtonText?: string;
} & DialogFooterProps;

const CustomDialogFooter = ({ cancelButtonText = 'Cancel', children, ...customDialogFooterProps }: CustomDialogFooterProps) => {
	return (
		<Dialog.Footer {...customDialogFooterProps}>
			<Dialog.ActionTrigger asChild>
				<Button flex={1} variant={'outline'}>
					{cancelButtonText}
				</Button>
			</Dialog.ActionTrigger>
			{children}
		</Dialog.Footer>
	);
};

const CustomDialogPortal = ({ children }: PropsWithChildren) => {
	return (
		<Portal>
			<Dialog.Backdrop />
			<Dialog.Positioner>{children}</Dialog.Positioner>
		</Portal>
	);
};

const CustomDialogTrigger = ({ children, ...customDialogTrigger }: DialogTriggerProps) => {
	return (
		<Dialog.Trigger asChild {...customDialogTrigger}>
			{children}
		</Dialog.Trigger>
	);
};

const CustomDialogRoot = ({ children, ...dialogRootProps }: DialogRootProps) => {
	return <Dialog.Root {...dialogRootProps}>{children}</Dialog.Root>;
};

const CustomDialog = {
	Root:    CustomDialogRoot,
	Trigger: CustomDialogTrigger,
	Portal:  CustomDialogPortal,
	Content: CustomDialogContent,
	Header:  CustomDialogHeader,
	Body:    CustomDialogBody,
	Footer:  CustomDialogFooter,
};
export default CustomDialog;
